import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Bell from '../../../assets/images/Bell.svg';
import Chat from '../../../assets/images/ClientChat.svg';
import Logo from '../../../assets/images/Logo.svg';
import LogoSmall from '../../../assets/images/PawLogo.png';

import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import HelpIcon from '@mui/icons-material/Help';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PetsIcon from '@mui/icons-material/Pets';
import PinDropIcon from '@mui/icons-material/PinDrop';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from 'context/AuthContext';
import { useCurrentBaseUser } from '../../../hooks/user/useCurrentBaseUser';

const ClientRoutePaths = {
  MESSAGES: '/client-messages',
  PROFILE: '/profile',
  DASHBOARD: '/dashboard',
  BOOKINGS: '/client-bookings',
  SETTINGS: '/settings',
  LOGOUT: '/logout',
  PETS: '/my-pets',
  HELP_CENTER: '/help-center',
};

interface DropdownMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  handleMenuPathClick: (value: string) => void;
}

const DropdownMenu = React.memo((props: DropdownMenuProps) => {
  const { anchorEl, open, onClose, handleMenuPathClick } = props;
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        mt: 1,
        '& .MuiPaper-root': {
          borderRadius: 2,
          minWidth: 250,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.PROFILE)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <AccountCircleOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Profile</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.DASHBOARD)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <PinDropIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Find Sitters</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.BOOKINGS)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <LibraryBooksIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>My Bookings</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.PETS)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <PetsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>My Pets</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.SETTINGS)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <SettingsOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Settings</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.HELP_CENTER)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <HelpIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Help Center</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={() => handleMenuPathClick(ClientRoutePaths.LOGOUT)}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(23, 139, 142, 0.08)',
          },
        }}
      >
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Log out</ListItemText>
      </MenuItem>
    </Menu>
  );
});

interface ClientHeaderProps {
  messageCount?: number;
  notificationCount?: number;
  userPhoto?: string;
  userName?: string;
}

const ClientHeader = React.memo((props: ClientHeaderProps) => {
  const { messageCount = 2, notificationCount = 3 } = props;

  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const { data: user } = useCurrentBaseUser();

  const { firstName, photoUrl } = user || {};

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuPathClick = (value: string) => {
    setAnchorEl(null);
    if (value === ClientRoutePaths.LOGOUT) {
      logout();
    } else {
      navigate(value);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        height: 70,
        backgroundColor: 'white',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, sm: 4 },
        zIndex: 1000,
      }}
    >
      {/* Left Side - Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
        }}
        onClick={() => handleMenuPathClick(ClientRoutePaths.DASHBOARD)}
      >
        <img
          src={isBelowMd ? LogoSmall : Logo}
          alt="HappyPaws Logo"
          style={{ width: isBelowMd ? '40px' : '205px', height: isBelowMd ? '44px' : '45px' }}
        />
      </Box>

      {/* Right Side - Icons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Messages Icon */}
        <IconButton
          onClick={() => handleMenuPathClick(ClientRoutePaths.MESSAGES)}
          sx={{
            color: '#5A5A5A',
            '&:hover': {
              backgroundColor: 'rgba(23, 139, 142, 0.08)',
            },
          }}
        >
          <Badge
            badgeContent={messageCount}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#178B8E',
                color: 'white',
                fontSize: '0.7rem',
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <img src={Chat} width={24} height={24} alt="Chat Icon" />
          </Badge>
        </IconButton>

        {/* Notifications Icon */}
        <IconButton
          onClick={() => console.log('dropdown notifications')}
          sx={{
            color: '#5A5A5A',
            '&:hover': {
              backgroundColor: 'rgba(23, 139, 142, 0.08)',
            },
          }}
        >
          <Badge
            badgeContent={notificationCount}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#178B8E',
                color: 'white',
                fontSize: '0.7rem',
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <img src={Bell} width={24} height={24} alt="Notifications Icon" />
          </Badge>
        </IconButton>

        {/* User Menu Button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          onClick={handleMenuClick}
          sx={{
            minWidth: 120,
            border: '1px solid gray',
            p: 0.5,
            gap: 2,
            borderRadius: 4,
            cursor: 'pointer',
            backgroundColor: open ? 'rgba(23, 139, 142, 0.08)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(64, 78, 78, 0.08)',
            },
          }}
        >
          <MenuIcon />
          <Avatar
            src={photoUrl || undefined}
            alt={firstName}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: '#178B8E',
              fontSize: '0.9rem',
            }}
          >
            {photoUrl ? '' : 'U'}
          </Avatar>
        </Stack>

        {/* Dropdown Menu */}
        <DropdownMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          handleMenuPathClick={handleMenuPathClick}
        />
      </Box>
    </Box>
  );
});

export default ClientHeader;
